import { useFundSectionNotice } from './fundSectionNotices'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ErrorState } from '../state'
import FolderDocuments from './FolderDocuments'
import { recordHref, type ClientView } from './context'
import { useRead } from './useRead'
import {
  fundClientContexts,
  matchedFundFolders,
  type FundClientHint,
  type FundClientContext,
} from '../fund/overviewFacts'

function ClientFacts({
  fundId,
  context,
  suppliedClient,
  selectedClientId,
  multiple,
  contextListLoading,
  contextListUnavailable,
  refresh,
  onRefresh,
}: {
  fundId: string
  context: FundClientContext
  suppliedClient?: ClientView | null
  selectedClientId?: string | null
  multiple: boolean
  contextListLoading: boolean
  contextListUnavailable: boolean
  refresh: number
  onRefresh: () => void
}) {
  const [params] = useSearchParams()
  const [folderId, setFolderId] = useState<string | null>(null)
  const supplied = suppliedClient?.id === context.id ? suppliedClient : null
  const read = useRead<ClientView>(
    supplied ? null : `/api/clients/${encodeURIComponent(context.id)}`,
    refresh
  )
  useFundSectionNotice(
    contextListUnavailable || read.error
      ? 'Client folders unavailable'
      : contextListLoading || (!supplied && read.loading)
        ? 'Loading client folders…'
        : null
  )
  const client = supplied ?? read.value
  const folders = client ? matchedFundFolders(fundId, context, client) : []
  const available = !!client && folders.length > 0
  const selectedFolder = folders.find((folder) => folder.id === folderId)
  const name = client?.id === context.id ? client.display_name : context.name
  const next = new URLSearchParams(params)
  next.set('client', context.id)
  const selected = selectedClientId === context.id
  return (
    <>
      <div data-testid="fund-client-fact" data-client-id={context.id}>
        <dt>{multiple ? 'Client context' : 'Client'}</dt>
        <dd>
          {available ? (
            <Link to={recordHref({ kind: 'client', client_id: context.id })}>
              {name?.trim() || 'Client name not recorded'} ›
            </Link>
          ) : (
            name?.trim() || 'Client name not recorded'
          )}
          {multiple && available ? (
            <small>
              {selected ? (
                'Selected context'
              ) : (
                <Link
                  className="nav-link"
                  to={`/v2/funds/${encodeURIComponent(fundId)}?${next}`}
                  data-testid="fund-client-context-choice"
                  aria-label={`Use ${name?.trim() || 'this client'} for this fund`}
                >
                  Use this client
                </Link>
              )}
            </small>
          ) : null}
          {client && !available ? <small>The folder no longer matches this fund.</small> : null}
          {contextListLoading ? <small role="status">Loading other client contexts…</small> : null}
          {contextListUnavailable ? <small>Other client contexts unavailable.</small> : null}
        </dd>
      </div>
      <div data-testid="fund-source-fact" data-client-id={context.id}>
        <dt>{folders.length > 1 ? 'Source folders' : 'Source folder'}</dt>
        <dd>
          {read.loading && !supplied ? 'Loading…' : null}
          {read.error ? (
            <ErrorState what="client folders" error={read.error} onRetry={onRefresh} compact />
          ) : null}
          {!read.loading && !read.error && !available ? 'Not available' : null}
          {folders.map((folder) => (
            <div key={folder.id}>
              <button
                type="button"
                className="nav-link"
                onClick={() => setFolderId(folder.id)}
                data-testid="fund-source-folder"
                data-folder-id={folder.id}
              >
                {client!.source.name} · {folder.folder_reference} ›
              </button>
              {folder.match_status === 'ambiguous' ? (
                <small>This folder matches more than one fund.</small>
              ) : null}
            </div>
          ))}
          {client && !available ? (
            <button type="button" className="nav-link" onClick={onRefresh}>
              Refresh
            </button>
          ) : null}
          {client && selectedFolder ? (
            <FolderDocuments
              key={`${client.id}:${selectedFolder.id}`}
              client={client}
              folder={selectedFolder}
              onClose={() => setFolderId(null)}
            />
          ) : null}
        </dd>
      </div>
    </>
  )
}

export default function FundClientFacts({
  fundId,
  hints,
  selectedClient,
  selectedClientId,
  loading = false,
  error,
  refresh = 0,
  onRefresh,
}: {
  fundId: string
  hints: readonly FundClientHint[] | null
  selectedClient?: ClientView | null
  selectedClientId?: string | null
  loading?: boolean
  error?: unknown
  refresh?: number
  onRefresh: () => void
}) {
  const contexts = fundClientContexts(fundId, hints ?? [], selectedClient)
  if (!contexts.length)
    return (
      <>
        <div data-testid="fund-client-fact">
          <dt>Client</dt>
          <dd>
            {loading ? 'Loading…' : error ? 'Client information unavailable' : 'Not recorded'}
          </dd>
        </div>
        <div data-testid="fund-source-fact">
          <dt>Source folder</dt>
          <dd>
            {loading ? (
              'Loading…'
            ) : error ? (
              <ErrorState what="client folders" error={error} onRetry={onRefresh} compact />
            ) : (
              'Not recorded'
            )}
          </dd>
        </div>
      </>
    )
  return contexts.map((context, index) => (
    <ClientFacts
      key={`${fundId}:${context.id}`}
      fundId={fundId}
      context={context}
      suppliedClient={selectedClient}
      selectedClientId={selectedClientId}
      multiple={contexts.length > 1}
      contextListLoading={loading && index === 0}
      contextListUnavailable={!!error && index === 0}
      refresh={refresh}
      onRefresh={onRefresh}
    />
  ))
}
